/**
 * The running timer, on every tab.
 *
 * Without it a timer started on Time vanished the moment you went to Tasks or
 * Entries — still running, but with nothing on screen to say so, and so easy
 * to forget until it had run all evening. Toggl keeps a bar like this above
 * its tab bar for exactly that reason.
 *
 * Not shown on Time itself, where the running card already fills the top of
 * the screen. Tap the bar for the full sheet; the stop button logs straight
 * away.
 */

import { useLocation } from "react-router-dom";

import { StopIcon } from "@/components/NavIcons";
import { formatElapsed, useTimer } from "@/context/TimerContext";
import { useAllItems, useProjects } from "@/hooks/useData";

import { useLogSheet } from "./LogSheetContext";

const TIME_PATHS = new Set(["/", "/time"]);

export function TimerBar() {
  const timer = useTimer();
  const { openTimer } = useLogSheet();
  const { pathname } = useLocation();
  const { data: projects = [] } = useProjects();
  const { data: items = [] } = useAllItems();

  if (!timer.running || TIME_PATHS.has(pathname)) return null;

  const project = projects.find((p) => p.id === timer.meta.projectId);
  const task = items.find((i) => i.id === timer.meta.itemId);
  // The description you'd see on the entry, else the most specific thing known.
  const title = timer.meta.note?.trim() || task?.title || project?.name || timer.meta.category || "Timer running";
  const sub = project?.name && title !== project.name ? project.name : timer.meta.category || (project ? "" : "No project");

  return (
    <div
      className="no-print"
      style={{
        position: "fixed", zIndex: 39, left: 12, right: 12,
        bottom: "calc(var(--tabbar-offset) + var(--tabbar-h) + 8px)",
        display: "flex", alignItems: "center", gap: 10, padding: "8px 8px 8px 14px",
        borderRadius: 16, background: "var(--accent)", color: "var(--onAccent)",
        boxShadow: "0 8px 24px rgba(20,22,26,0.18)",
      }}
    >
      <button
        onClick={openTimer}
        aria-label="Open the running timer"
        style={{
          flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10,
          border: "none", background: "none", color: "inherit", padding: 0, textAlign: "left", cursor: "pointer",
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: project?.color ?? "var(--onAccent)", flex: "none", boxShadow: "0 0 0 2px var(--onAccent)" }} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
          {sub && <span style={{ display: "block", fontSize: 11.5, opacity: 0.85, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</span>}
        </span>
        <span className="mono" style={{ flex: "none", fontSize: 16, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
          {formatElapsed(timer.seconds)}
        </span>
      </button>
      <button
        onClick={() => void timer.stop()}
        aria-label="Stop and log"
        style={{
          flex: "none", width: 40, height: 40, borderRadius: "50%", border: "none", cursor: "pointer",
          background: "var(--onAccent)", color: "var(--accent)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      ><StopIcon size={16} /></button>
    </div>
  );
}
